import { ChevronLeft } from 'lucide-react';
import type { ReactNode } from 'react';
import type { Me } from '../api/auth';
import { FOOTER_LINKS } from '../lib/legalDocuments';
import { AccountMenu } from './AccountMenu';

interface AppBarProps {
  /** Shown beside the back chevron — the object you came from. */
  parentLabel: string;
  title?: string;
  subtitle?: string;
  onBack?: () => void;
  /**
   * The signed-in person, so the bar can carry the same account control every other page has.
   * Optional only because a bar can render before the session resolves; when it is absent the
   * right-hand side is empty rather than holding a placeholder.
   */
  me?: Me;
  /** Which unit's page this is, so the menu can mark it and offer that unit's Manage. */
  currentUnitId?: string;
  onNavigate?: (path: string) => void;
}

/**
 * The detail-page header: back, what you are looking at, and the account control — spec §5A,
 * surface-inverse with 13px vertical padding.
 *
 * <b>The Bell and the Ellipsis are gone</b> (TROOP-122). Both had sat here since the bar was first
 * built and neither had ever been wired to anything: two controls that looked like features,
 * occupying the exact corner where this product's account control lives on every other page. They
 * are removed rather than implemented, because there is no notification centre and no overflow menu
 * behind them to reach — and a control with nothing behind it is worse than a missing one, since
 * the only way to discover it does nothing is to press it.
 *
 * What replaces them is {@link AccountMenu}, the same component `AppHeader` uses, in the same
 * place. The two headers now differ in exactly one thing: this one trades the wordmark and the
 * three destination icons for a back button and the name of what you are reading.
 */
export function AppBar({
  parentLabel,
  title,
  subtitle,
  onBack,
  me,
  currentUnitId,
  onNavigate,
}: AppBarProps) {
  return (
    <header style={{ background: 'var(--color-surface-inverse)' }}>
      <div
        className="mx-auto flex items-center gap-2"
        style={{
          maxWidth: '46rem',
          padding: '13px var(--gutter)',
          color: 'var(--color-text-on-inverse)',
        }}
      >
        <button
          type="button"
          onClick={onBack}
          aria-label={`Back to ${parentLabel}`}
          className="inline-flex items-center gap-1 shrink-0 rounded-md"
          style={{
            minHeight: 'var(--tap-min)',
            minWidth: 'var(--tap-min)',
            marginInlineStart: 'calc(var(--space-2) * -1)',
            color: 'inherit',
            background: 'none',
            border: 'none',
          }}
        >
          <ChevronLeft size={20} aria-hidden="true" />
          <span className="t-caption truncate">{parentLabel}</span>
        </button>

        <div className="flex-1 min-w-0 text-center">
          {title && (
            <p className="t-body-strong truncate" style={{ margin: 0 }}>
              {title}
            </p>
          )}
          {subtitle && (
            <p className="t-caption truncate" style={{ margin: 0, opacity: 0.82 }}>
              {subtitle}
            </p>
          )}
        </div>

        <div className="flex items-center shrink-0">
          {me && onNavigate && (
            <AccountMenu me={me} currentUnitId={currentUnitId} onNavigate={onNavigate} />
          )}
        </div>
      </div>
    </header>
  );
}

/**
 * The disclaimer is fixed text and appears in **every** page footer, on sign-up,
 * and in About — same wording every time, never abbreviated, never inside a
 * collapsed accordion (spec §8, §10).
 */
export const DISCLAIMER = 'Not affiliated with or endorsed by Scouting America.';

/**
 * The page footer: the disclaimer, then the three real destinations beneath it — About, Privacy,
 * Terms, in that order (§8, which puts the disclaimer above the legal links).
 *
 * <b>Privacy and Terms point at documents now</b> (TROOP-203). They spent a year as `#privacy` and
 * `#terms`, anchors with no route, no screen and no content behind either anywhere in the
 * codebase, so pressing one scrolled the page and did nothing — on every page in the product.
 * TROOP-217 deleted them rather than faking them, because a privacy policy and terms have to be
 * real documents checked line by line against what the code actually does and a policy that
 * overstates protection is worse than none; this is the ticket that wrote the documents. About was
 * the third dead anchor and is the one TROOP-217 built instead, since §10 and {@link DISCLAIMER}'s
 * own doc comment both already required it.
 *
 * <b>The list and the routes are one list.</b> `FOOTER_LINKS` is read here and in `App`, where a
 * `Record<FooterPath, ReactElement>` turns a destination with nothing behind it into a compile
 * error.
 * That is deliberately structural: what went wrong here was not that somebody wrote a bad link, it
 * was that nothing in the build, the tests or the browser could tell anyone the link was dead.
 *
 * <b>A plain `href`, so each of these is a document load rather than a client-side transition.</b>
 * Every other in-app navigation threads an `onNavigate` callback, and `Footer` takes no props on
 * thirty screens — adding one to reach pages most readers open once is a worse trade than the
 * reload. `HomeScreen`'s "Sign in" link is the same call.
 */
export function Footer() {
  return (
    <footer
      style={{
        background: 'var(--color-surface-sunken)',
        borderTop: '1px solid var(--color-border-subtle)',
        marginTop: 'auto',
      }}
    >
      <div
        className="mx-auto flex flex-col gap-3"
        style={{ maxWidth: '46rem', padding: 'var(--space-6) var(--gutter)' }}
      >
        <p
          style={{
            font: 'var(--text-caption)',
            color: 'var(--color-text-secondary)',
            margin: 0,
          }}
        >
          {DISCLAIMER}
        </p>

        {/* Real tap targets (§10, "without exception"). The three dead anchors were ~19px line
            boxes, which nobody minded while pressing one did nothing; these navigate, so they get
            the height. The negative inline margin keeps the first label optically flush with the
            disclaimer above it rather than indenting the whole row by its own padding — it is on
            the row rather than on each link, so the second and third stay where the padding puts
            them. */}
        <nav
          className="flex flex-wrap gap-x-2 gap-y-1"
          style={{
            marginBlockEnd: '-10px',
            marginInlineStart: 'calc(var(--space-2) * -1)',
          }}
        >
          {FOOTER_LINKS.map((link) => (
            <a
              key={link.path}
              className="t-caption inline-flex items-center rounded-md"
              href={link.path}
              style={{ minHeight: 'var(--tap-min)', paddingInline: 'var(--space-2)' }}
            >
              {link.label}
            </a>
          ))}
        </nav>
      </div>
    </footer>
  );
}

export function Page({ children }: { children: ReactNode }) {
  return (
    <div
      className="flex min-h-screen flex-col"
      style={{ background: 'var(--color-surface-page)' }}
    >
      {children}
    </div>
  );
}

export function Gutter({ children, className = '' }: { children: ReactNode; className?: string }) {
  return (
    <div
      className={`mx-auto w-full ${className}`}
      style={{ maxWidth: '46rem', paddingInline: 'var(--gutter)' }}
    >
      {children}
    </div>
  );
}
